/**
 * @file index.jsx
 * @author Rishikesh
 * @date 2024-09-11
 * @description Manage the user details.
 */
import { Box, Button, Skeleton, Stack } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useSnackbar } from "notistack";

import { HeaderSection } from "./HeaderSection";
import { FormSection } from "./FormSection";
import { DetailsPanel } from "./DetailsPanel";
import { useFetchData } from "../../../components/hooks/useFetchData";
import { LoadingBackdrop } from "../../../components/common/LoadingBackdrop";

const ManageUser = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const user = location.state?.user;
  const [minLoading, setMinLoading] = useState(true);
  const [countryList, setCountryList] = useState([]);
  const [courseList, setCourseList] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState();
  const [courseCountry, setCourseCountry] = useState([]);
  const { fetchData, loading } = useFetchData();
  const { enqueueSnackbar } = useSnackbar();
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
  } = useForm({
    defaultValues: {
      useruuid: user?.useruuid || "",
      name: user?.name || "",
      email: user?.email || "",
      role: user?.role || 10,
      officephone: user?.officephone || "",
      personalphone: user?.personalphone || "",
      isactive: user?.isactive || 0,
    },
  });

  useEffect(() => {
    if (user) {
      setCourseCountry(user?.coursecountry || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async (data) => {
    const body = {
      ...data,
      coursecountry: courseCountry,
    };
    const result = await fetchData("manageuser", {
      method: "post",
      body: body,
    });
    if (result?.code === 200) {
      enqueueSnackbar(result.message || "Successfull!", {
        variant: "success",
      });
      navigate(-1);
    } else {
      enqueueSnackbar(result.message || "Something went wrong!", {
        variant: "error",
      });
    }
  };

  const getAllCountries = async () => {
    const result = await fetchData("getcountries?showActive=1");
    if (result?.code === 200) {
      setCountryList(result.data);
    }
  };

  const getCoursesByCountry = async () => {
    if (!selectedCountry) return;
    const body = { countryid: selectedCountry?.id };
    const result = await fetchData("getcoursesbycountry", {
      method: "post",
      body: body,
    });
    if (result?.code === 200) {
      setCourseList(result.data);
    }
  };

  React.useEffect(() => {
    getAllCountries();
    getCoursesByCountry();
    const minLoadingTimer = setTimeout(() => {
      setMinLoading(false);
    }, 1500);

    return () => clearTimeout(minLoadingTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const fetchDataAsync = async () => {
      await getCoursesByCountry();
    };

    fetchDataAsync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountry]);

  const isLoading = minLoading || loading["getcountries?showActive=0"];

  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        width="100%"
        height="100%"
        overflow={{
          xs: "auto",
          md: "hidden",
        }}
      >
        <HeaderSection id={user?.useruuid || ""} isLoading={isLoading} />
        <Stack
          pt={3}
          flex={1}
          overflow={{ xs: "unset", md: "hidden" }}
          direction={{ xs: "column", md: "row" }}
          columnGap={1}
          rowGap={2}
        >
          <FormSection
            isLoading={isLoading}
            register={register}
            errors={errors}
            watch={watch}
            setValue={setValue}
          />
          <DetailsPanel
            isLoading={isLoading}
            countryList={countryList}
            courseList={courseList}
            selectedCountry={selectedCountry}
            setSelectedCountry={setSelectedCountry}
            courseCountry={courseCountry}
            setCourseCountry={setCourseCountry}
          />
        </Stack>
        <Stack
          width="100%"
          mt={2}
          direction="row"
          alignItems="center"
          justifyContent="end"
          spacing={2}
        >
          {isLoading ? (
            <Skeleton variant="rounded" width={160} height={40} />
          ) : (
            <Button
              variant="outlined"
              sx={{ borderRadius: "12px", minWidth: 160, py: 1.2 }}
              onClick={() => {
                reset();
                setSelectedCountry();
                setCourseCountry(user?.coursecountry);
              }}
            >
              Reset
            </Button>
          )}
          {isLoading ? (
            <Skeleton variant="rounded" width={160} height={40} />
          ) : (
            <Button
              onClick={handleSubmit(onSubmit)}
              variant="contained"
              sx={{ borderRadius: "12px", minWidth: 160, py: 1.2 }}
            >
              Save
            </Button>
          )}
        </Stack>
      </Box>
      <LoadingBackdrop isLoading={loading["manageuser"]} />
    </>
  );
};

export default ManageUser;
